import moment from 'moment'
import { getFuturePayments } from './getFuturePayments'
import isRentlioPayEnabled from './helpers/isRentlioPayEnabled'
import { FIRST_NIGHT } from 'Rentlio/utils/CancellationPolicyFeeTypeEnum'
import { logErrorWithJsonData } from 'Rentlio/utils/logging'
import { DIRECT } from 'Rentlio/utils/scheduledPaymentTypeEnum'

// Aggregates all scheduled payments from accommodation items in the receipt.

// If a booking includes multiple reservations (group booking),
// this function combines all payment amounts into a maximum of two payment entries:
//   - One for the prepayment
//   - One for the remaining balance (if applicable)

// All reservations are expected to follow the same cancellation policy,
// meaning that payment types and timestamps should match across items.
// Inconsistent payment definitions (different types or timestamps) will be logged.

const groupScheduledPayments = (items, arrival, isAutoPayEnabled) => {
  if (!isRentlioPayEnabled()) return []

  let error = null
  if (!(items?.length > 0)) {
    error = 'Invalid receipt items'
  }

  let firstPayment = null
  let secondPayment = null

  items.forEach(item => {
    // Amounts are sent exactly as received: the receipt is priced in the property's main (invoice)
    // currency, which is the only currency the backend validates, persists, and charges scheduled
    // payments in. The guest-selected currency is applied at display time only.
    const { price: totalRatePrice, servicesCost, firstNightPrice, currency } = item

    const { amountWillBePaidDaysBefore, prepaymentAmountDueDate, prepaymentAmountType, prepaymentAmount } =
      item.cancellationPolicy

    const prepaymenyAmountPercentage = prepaymentAmountType === FIRST_NIGHT ? null : Number(prepaymentAmount)

    const itemScheduledPayments = getFuturePayments(
      arrival,
      totalRatePrice + servicesCost,
      currency,
      amountWillBePaidDaysBefore,
      prepaymentAmountType,
      prepaymenyAmountPercentage,
      prepaymentAmountDueDate,
      firstNightPrice
    )

    if (itemScheduledPayments && itemScheduledPayments.length === 0) {
      return []
    }

    if (
      itemScheduledPayments[0] &&
      !itemScheduledPayments[0].excludeFromScheduling &&
      (isAutoPayEnabled || itemScheduledPayments[0].type === DIRECT)
    ) {
      if (!firstPayment) {
        firstPayment = itemScheduledPayments[0]
      } else if (
        (!firstPayment.timestamp || firstPayment.timestamp === itemScheduledPayments[0].timestamp) &&
        (!firstPayment.type || firstPayment.type === itemScheduledPayments[0].type)
      ) {
        firstPayment.amount += itemScheduledPayments[0].amount
        firstPayment.amountCents += itemScheduledPayments[0].amountCents
        firstPayment.currency = currency
        firstPayment.timestamp = itemScheduledPayments[0].timestamp
        firstPayment.type = itemScheduledPayments[0].type
      } else {
        // log error itemScheduledPayments[0] empty or timestamps are different or type is not the same
        error = 'First payments in a group booking have different timestamps or types'
      }
    }

    if (itemScheduledPayments[1] && !itemScheduledPayments[1].excludeFromScheduling && isAutoPayEnabled) {
      if (!secondPayment) {
        secondPayment = itemScheduledPayments[1]
      } else if (
        secondPayment.timestamp === itemScheduledPayments[1].timestamp &&
        secondPayment.type === itemScheduledPayments[1].type
      ) {
        secondPayment.amount += itemScheduledPayments[1].amount
        secondPayment.amountCents += itemScheduledPayments[1].amountCents
        secondPayment.currency = currency
        secondPayment.timestamp = itemScheduledPayments[1].timestamp
        secondPayment.type = itemScheduledPayments[1].type
      } else {
        // log error or timestamps are different IMPORTANT: itemScheduledPayments[1] will be empty if isAmountWillBePaidDaysBeforeInThePast is true
        error = `Second payments in a group booking have different timestamps or types`
      }
    }
  })

  if (error) {
    logErrorWithJsonData(
      `RentlioPay groupScheduledPayments error`,
      {
        items
      },
      {
        critical: 'true',
        description: error
      }
    )

    return []
  }
  if (firstPayment && secondPayment) {
    return [firstPayment, secondPayment]
  }

  if (firstPayment) {
    return [firstPayment]
  }

  return []
}

export const getScheduledPaymentsPayload = (receipt, arrivalDate, isAutoPayEnabled) => {
  const arrival = moment.utc(arrivalDate * 1000)

  const items = receipt.accomodation.items.map(
    ({ price, servicesCost, firstNightPrice, cancellationPolicy, currency }) => ({
      price,
      servicesCost,
      firstNightPrice,
      cancellationPolicy,
      currency
    })
  )

  return groupScheduledPayments(items, arrival, isAutoPayEnabled)
}

export const getScheduledOfferPaymentsPayload = (receipt, isAutoPayEnabled) => {
  const earliestArrival = receipt?.items.reduce((acc, { arrival }) => {
    if (acc === null) return arrival
    if (arrival.isBefore(acc)) return arrival
    return acc
  }, null)

  const items = receipt.items.map(
    ({ totalRateCost, totalServicesCost, firstNightPrice, cancellationPolicy, currency }) => ({
      price: totalRateCost,
      servicesCost: totalServicesCost,
      firstNightPrice,
      cancellationPolicy,
      currency
    })
  )

  return groupScheduledPayments(items, earliestArrival, isAutoPayEnabled)
}
